import { inject } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { BreadcrumbsItem, BreadcrumbsViewModel } from 'src/common/models/common/breadcrumbs-model';

import { AnalyticsConfig, classNames } from 'uitk-react-utils';
import { UitkBreadcrumbs } from 'uitk-react-breadcrumbs';
import { UitkCardAloha, UitkCardAlohaContentSection } from 'uitk-react-cards';
import { UitkSpacing } from 'uitk-react-spacing';

import { SchemaOrgScript, StructuredData } from 'components/common/schema-org-script/schema-org-script';

/**
 * Decorates BreadcrumbsViewModel with an implementation of StructuredData to transform the
 * model into a Structured Data model.
 * It enhances de model with utility methods like `isEmpty` to leverage encapsulation.
 */
class BreadcrumbViewModelSchemaAware implements BreadcrumbsViewModel, StructuredData {
  crumbs: BreadcrumbsItem[];
  crumbsToDisplay?: number;
  className?: string;
  menuAnalytics?: AnalyticsConfig;
  structuredData: any;

  constructor(original: BreadcrumbsViewModel) {
    this.crumbs = original.crumbs;
    this.crumbsToDisplay = original.crumbsToDisplay;
    this.className = original.className;
    this.menuAnalytics = original.menuAnalytics;
    this.structuredData = {
      '@context': 'http://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: original.crumbs.map(crumb => ({
        '@type': 'ListItem',
        name: crumb.text,
        position: crumb.index,
        item: crumb.fullUrl,
      })),
    };
  }

  stringOfStructuredData = () => JSON.stringify(this.structuredData);
}

interface BreadcrumbsProps {
  breadcrumbsContent?: BreadcrumbsViewModel;
  isDateless?: boolean;
  analytics?: AnalyticsStore;
}

interface DisplayBreadcrumbsProps {
  crumbsClasses?: string;
  crumbsContent: BreadcrumbsItem[];
  crumbsToDisplay?: number;
  handleClick?: () => void;
  menuAnalytics?: AnalyticsConfig;
  showMenu?: boolean;
  structuredData: StructuredData;
}

interface DisplayBreadcrumbsState {
  showMenu: boolean;
  crumbsToDisplay: {
    viewSmall?: number;
    viewMedium?: number;
  };
}

@inject('analytics')
export class Breadcrumbs extends React.Component<BreadcrumbsProps, DisplayBreadcrumbsState> {
  constructor(props: BreadcrumbsProps) {
    super(props);

    this.state = {
      showMenu: false,
      crumbsToDisplay: {
        viewSmall: null,
        viewMedium: null,
      },
    };
    this.handleClick = this.handleClick.bind(this);
  }

  public componentDidMount() {
    this.setState({
      crumbsToDisplay: {
        viewSmall: 1,
        viewMedium: 2,
      },
    });
  }

  public handleClick() {
    this.setState({
      showMenu: !this.state.showMenu,
    });
  }

  public render() {
    const { breadcrumbsContent, isDateless, analytics } = this.props;
    const { showMenu, crumbsToDisplay } = this.state;

    if (!breadcrumbsContent || !breadcrumbsContent.crumbs || breadcrumbsContent.crumbs.length === 0 || !isDateless) {
      return null;
    }

    const decoratedBreadcrumbsContent = new BreadcrumbViewModelSchemaAware(breadcrumbsContent);

    decoratedBreadcrumbsContent.crumbs.forEach((crumb, index) => {
      decoratedBreadcrumbsContent.crumbs[index].analytics = analytics.setupTracker(
        'BreadcrumbItem.Click',
        'User interacts with breadcrumb item'
      );
    });

    const menuAnalytics: AnalyticsConfig = analytics.setupTracker('Breadcrumbs.Menu', 'User interacts with breadcrumbs menu');

    const commonProps = {
      crumbsContent: decoratedBreadcrumbsContent.crumbs,
      handleClick: this.handleClick,
      menuAnalytics,
      showMenu,
      structuredData: decoratedBreadcrumbsContent,
    };

    return (
      <Viewport>
        <ViewSmall>
          <DisplayBreadcrumbs crumbsToDisplay={crumbsToDisplay.viewSmall} {...commonProps} />
        </ViewSmall>
        <ViewMedium>
          <DisplayBreadcrumbs crumbsToDisplay={crumbsToDisplay.viewMedium} {...commonProps} />
        </ViewMedium>
        <ViewLarge>
          <DisplayBreadcrumbs crumbsClasses="uitk-card-separator-bottom" {...commonProps} />
        </ViewLarge>
      </Viewport>
    );
  }
}

export const DisplayBreadcrumbs = ({
  crumbsClasses,
  crumbsContent,
  crumbsToDisplay,
  handleClick,
  menuAnalytics,
  showMenu,
  structuredData,
}: DisplayBreadcrumbsProps) => {
  if (!crumbsContent || crumbsContent.length === 0) {
    return null;
  }

  const cssClasses = classNames('uitk-cell uitk-flat-border-top uitk-flat-border-bottom', crumbsClasses);

  return (
    <>
      <SchemaOrgScript structuredData={structuredData} />
      <UitkCardAloha className={cssClasses} data-stid="breadcrumbs" overflow={true}>
        <UitkCardAlohaContentSection padded={false}>
          <UitkSpacing padding={{ inline: 'three', block: 'one' }}>
            <div>
              <UitkBreadcrumbs
                crumbs={crumbsContent}
                crumbsToDisplay={crumbsToDisplay}
                isOpen={showMenu}
                onTriggerClick={handleClick}
                menuAnalytics={menuAnalytics}
              />
            </div>
          </UitkSpacing>
        </UitkCardAlohaContentSection>
      </UitkCardAloha>
    </>
  );
};

DisplayBreadcrumbs.displayName = 'DisplayBreadcrumbs';
